import { useState, useEffect, useRef, useCallback, Dispatch } from "react";
import useEventListener from "@use-it/event-listener";

const globalState: { [key: string]: any } = {};

const createGlobalState = <T>(
  key: string,
  thisCallback: Dispatch<T>,
  initialValue: T
) => {
  if (!globalState[key]) {
    globalState[key] = { callbacks: [], value: initialValue };
  }
  globalState[key].callbacks.push(thisCallback);
  return {
    deregister() {
      const arr = globalState[key].callbacks;
      const index = arr.indexOf(thisCallback);
      if (index > -1) {
        arr.splice(index, 1);
      }
    },
    emit(value: T) {
      if (globalState[key].value !== value) {
        globalState[key].value = value;
        globalState[key].callbacks.forEach((callback: Dispatch<T>) => {
          if (thisCallback !== callback) {
            callback(value);
          }
        });
      }
    },
  };
};

const usePersistedState: <T>(
  initialState: T,
  key: string,
  get_set: {
    get: (key: string, initialValue: T) => T;
    set: (key: string, newValue: T) => void;
  }
) => [T, Dispatch<T>] = <T>(
  initialState: T,
  key: string,
  {
    get,
    set,
  }: {
    get: (key: string, initialValue: T) => T;
    set: (key: string, newValue: T) => void;
  }
) => {
  const globalState = useRef<{
    deregister: () => void;
    emit: Dispatch<T>;
  } | null>(null);
  // Use initialState for first render to match server and avoid hydration
  // mismatches. Sync persisted value from localStorage in the effect below.
  // Components that care about whether they are pre- or post-hydration should
  // call useHydrated() rather than trying to detect it from this value.
  const [state, setState] = useState(initialState);

  // subscribe to `storage` change events
  useEventListener("storage", ({ key: k, newValue }: any) => {
    if (k === key) {
      const newState = JSON.parse(newValue);
      if (state !== newState) {
        setState(newState);
      }
    }
  });

  // only called on mount
  useEffect(() => {
    // Sync persisted value after hydration
    const stored = get(key, initialState);
    if (stored !== initialState) setState(stored);

    // register a listener that calls `setState` when another instance emits
    globalState.current = createGlobalState(key, setState, state);

    return () => {
      globalState.current?.deregister();
    };
  }, [key]);

  const persistentSetState = useCallback(
    (newState: T) => {
      const newStateValue =
        typeof newState === "function" ? newState(state) : newState;

      // persist to localStorage
      set(key, newStateValue);

      setState(newStateValue);

      // inform all of the other instances in this tab
      globalState.current?.emit(newState);
    },
    [state, set, key]
  );

  return [state, persistentSetState];
};

const createStorage = <T>(provider: Storage) => ({
  get(key: string, defaultValue: T) {
    const json = provider.getItem(key);
    // eslint-disable-next-line no-nested-ternary
    return json === null || typeof json === "undefined"
      ? typeof defaultValue === "function"
        ? defaultValue()
        : defaultValue
      : JSON.parse(json);
  },
  set(key: string, value: T) {
    provider.setItem(key, JSON.stringify(value));
  },
});

const isStorage = (obj: any): obj is Storage =>
  obj && typeof obj.getItem === "function" && typeof obj.setItem === "function";

const getProvider = () => {
  if (typeof global !== "undefined" && isStorage(global.localStorage)) {
    return global.localStorage;
  }
  // eslint-disable-next-line no-undef
  if (typeof globalThis !== "undefined" && isStorage(globalThis.localStorage)) {
    // eslint-disable-next-line no-undef
    return globalThis.localStorage;
  }
  if (typeof window !== "undefined" && isStorage(window.localStorage)) {
    return window.localStorage;
  }
  if (typeof localStorage !== "undefined" && isStorage(localStorage)) {
    return localStorage;
  }
  return null;
};

const createPersistedState: <T>(
  key: string,
  provider?: Storage
) => (initialState: T) => [T, Dispatch<T>] = <T>(
  key: string,
  provider = getProvider()
) => {
  if (provider) {
    const storage = createStorage(provider);
    return (initialState: T) => usePersistedState(initialState, key, storage);
  }
  return useState;
};

export default createPersistedState;
